// PerRunDetailsView — feature-by-feature breakdown for one specific run.
//
// Two ways in: from per-store history (modal content swap, parent passes
// `run` directly) or from a single-store on-demand result (inline below
// the audit form). Layout matches the description in tracker decision #22:
// 6 stacked Cloudscape `<Container>` blocks, one per category, each with
// a 2-column feature-card grid. Card colors match
// `generate_excel.py::score_fill` from the POC.

import React from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
} from '@cloudscape-design/components';

// Category order mirrors the POC's `generate_excel.py::CATEGORIES` exactly.
// Keep in sync when scoring rules change.
const CATEGORY_ORDER = [
  ['Homepage Widgets', 'homepage_widgets'],
  ['Store Features',   'store_features'],
  ['PDP',              'pdp'],
  ['Product Quality',  'product_quality'],
  ['Offers & Pricing', 'offers_and_pricing'],
  ['Policies',         'policies'],
];

// Feature → category mapping. Lifted from `generate_excel.py::CATEGORIES`.
// Keeps this component free of backend-knowledge — categories are stable
// even when scoring rules within them change.
const FEATURE_TO_CATEGORY = {
  // Homepage Widgets
  announcement_bar: 'homepage_widgets',
  hero_banner: 'homepage_widgets',
  bestselling_products: 'homepage_widgets',
  featured_collection: 'homepage_widgets',
  categories_preview: 'homepage_widgets',
  banners: 'homepage_widgets',
  testimonials: 'homepage_widgets',
  homepage_oos_penalty: 'homepage_widgets',
  hero_banner_blank_links: 'homepage_widgets',
  // Store Features
  trust_markers: 'store_features',
  phone_number: 'store_features',
  address: 'store_features',
  custom_navigation: 'store_features',
  social_media_linking: 'store_features',
  instagram_feed: 'store_features',
  logo: 'store_features',
  collections: 'store_features',
  google_reviews: 'store_features',
  video_commerce_homepage: 'store_features',
  video_commerce_pdp: 'store_features',
  custom_domain: 'store_features',
  homepage_lcp: 'store_features',
  pincode_serviceability: 'store_features',
  // PDP
  pdp_btf_layout: 'pdp',
  ratings_reviews: 'pdp',
  pdp_lcp: 'pdp',
  // Product Quality
  product_title: 'product_quality',
  product_description: 'product_quality',
  selling_price_discount: 'product_quality',
  size_chart: 'product_quality',
  oos_ratio: 'product_quality',
  // Offers & Pricing
  offers: 'offers_and_pricing',
  delivery_fees: 'offers_and_pricing',
  delivery_eta: 'offers_and_pricing',
  cod: 'offers_and_pricing',
  buy_with_amazon: 'offers_and_pricing',
  // Policies
  return_policy: 'policies',
};

export default function PerRunDetailsView({ run, mode = 'modal' }) {
  if (!run) {
    return (
      <Box variant="awsui-key-label" color="text-body-secondary">
        No run details available.
      </Box>
    );
  }

  if (run.status === 'failed') {
    return (
      <Box>
        <Box variant="awsui-key-label" color="text-status-error">
          ❌ This run failed
        </Box>
        <Box>{run.error || 'Unknown error'}</Box>
      </Box>
    );
  }

  // Bucket individual scores into the 6 categories. The run JSON itself
  // already carries `category_scores` aggregates, but we re-derive the
  // per-feature membership client-side so we don't depend on an exact
  // category-key shape from the backend.
  const featuresByCategory = {};
  for (const slug of CATEGORY_ORDER.map((c) => c[1])) {
    featuresByCategory[slug] = [];
  }
  const individual = run.individual_scores || {};
  for (const [name, blob] of Object.entries(individual)) {
    const slug = FEATURE_TO_CATEGORY[name] || 'other';
    if (!featuresByCategory[slug]) featuresByCategory[slug] = [];
    featuresByCategory[slug].push({ name, ...blob });
  }

  return (
    <SpaceBetween size="m">
      {CATEGORY_ORDER.map(([label, slug]) => {
        const features = featuresByCategory[slug] || [];
        if (!features.length) return null;
        const cat = run.category_scores?.[slug] || {};
        const headerLine =
          cat.score !== undefined && cat.max_score !== undefined
            ? `${label} — ${cat.score} / ${cat.max_score} (${cat.percentage ?? 0}%)`
            : label;
        return (
          <Container
            key={slug}
            header={<Header variant="h3">{headerLine}</Header>}
          >
            <div className="cro-audit-category-grid">
              {features.map((f) => (
                <FeatureCard key={f.name} feature={f} />
              ))}
            </div>
          </Container>
        );
      })}
    </SpaceBetween>
  );
}

function FeatureCard({ feature }) {
  const score = Number(feature.score ?? 0);
  const max = Number(feature.max_score ?? 0);
  const band = bandForFeature(score, max);
  const dataSource = feature.data_source || '';

  return (
    <div className={`cro-audit-feature-card ${band}`}>
      <div className="cro-audit-feature-card-header">
        <span>{prettyName(feature.name)}</span>
        <span className="cro-audit-feature-card-score">
          {score} / {max}
        </span>
      </div>
      <div className="cro-audit-feature-card-details">
        {feature.details || ''}
      </div>
      {dataSource && (
        <div className="cro-audit-feature-card-source">{dataSource}</div>
      )}
    </div>
  );
}

function bandForFeature(score, max) {
  if (max === 0) return 'grey';
  const pct = score / max;
  if (pct >= 0.8) return 'green';
  if (pct >= 0.5) return 'yellow';
  return 'red';
}

function prettyName(name) {
  return (name || '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
